/**
 * FormatMascot — per-format mascot shown in the event card's avatar circle
 * when the host didn't upload a photo. Uses the logo's mascot shape (head +
 * dome body) centred in a pastel circle, with a detailed, colourful prop
 * tucked against its lower-right side. Prop colours are deliberately picked
 * to contrast with the mascot body so the two don't blend into one blob.
 *
 * Custom (free-text) formats are matched to the closest known prop via a
 * keyword map; anything unrecognised falls back to the gold-star "other".
 */

type Color = "yellow" | "purple" | "blue" | "red" | "green";

const FILL: Record<Color, string> = {
  yellow: "#F59E0B",
  purple: "#7C3AED",
  blue: "#2563EB",
  red: "#EF4444",
  green: "#059669",
};
const SOFT: Record<Color, string> = {
  yellow: "#FEF3C7",
  purple: "#EDE9FE",
  blue: "#DBEAFE",
  red: "#FEE2E2",
  green: "#D1FAE5",
};
const O = "#2B2F54";
const W = "#FFFFFF";

// color = mascot body colour; prop colours chosen to contrast with it.
// prop is optional — "other"/custom formats show just the group of mascots
// with no held object.
const SPEC: Record<string, { color: Color; prop?: JSX.Element }> = {
  cafe: {
    color: "yellow",
    prop: (
      <g>
        <rect x="-10" y="-7" width="17" height="16" rx="2.5" fill={W} stroke={O} strokeWidth="2.5" />
        <path d="M 7 -3 q 6 0 6 6 q 0 6 -6 6" fill="none" stroke={O} strokeWidth="2.5" />
        <rect x="-10" y="-7" width="17" height="4" fill="#C2410C" />
        <path d="M -5 -13 q -2.5 -4 0 -8" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
        <path d="M 1 -13 q 2.5 -4 0 -8" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
      </g>
    ),
  },
  run: {
    color: "blue",
    // red sneaker — contrasts the blue mascot
    prop: (
      <g>
        <path d="M -14 4 q 0 -7 7 -8 l 4 -5 l 5 3 l 5 -1 l 4 6 l 7 0 q 5 0 5 5 z" fill="#EF4444" stroke={O} strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M -16 4 h 36 q 2 0 2 3 q 0 3 -3 3 h -34 q -2 0 -2 -3 z" fill={W} stroke={O} strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M -3 -6 l 3 4 M 3 -8 l 3 4" stroke={W} strokeWidth="2" strokeLinecap="round" />
      </g>
    ),
  },
  boardgames: {
    color: "purple",
    // white die, navy pips
    prop: (
      <g transform="rotate(8)">
        <rect x="-11" y="-11" width="22" height="22" rx="4" fill={W} stroke={O} strokeWidth="2.5" />
        <circle cx="-5" cy="-5" r="2.3" fill={O} />
        <circle cx="5" cy="-5" r="2.3" fill={O} />
        <circle cx="0" cy="0" r="2.3" fill={O} />
        <circle cx="-5" cy="5" r="2.3" fill={O} />
        <circle cx="5" cy="5" r="2.3" fill={O} />
      </g>
    ),
  },
  movie: {
    color: "blue",
    // popcorn tub: red/white stripes, yellow kernels — pops against blue mascot
    prop: (
      <g>
        <circle cx="-5" cy="-8" r="3.4" fill="#FDE047" stroke={O} strokeWidth="1.6" />
        <circle cx="1" cy="-10" r="3.4" fill="#FDE047" stroke={O} strokeWidth="1.6" />
        <circle cx="6" cy="-7" r="3" fill="#FDE047" stroke={O} strokeWidth="1.6" />
        <path d="M -9 -5 h 19 l -2.5 16 q -0.2 1.6 -1.8 1.6 h -10.4 q -1.6 0 -1.8 -1.6 z" fill={W} stroke={O} strokeWidth="2.2" strokeLinejoin="round" />
        <path d="M -4.5 -5 l -1 17" stroke="#EF4444" strokeWidth="2.4" />
        <path d="M 1 -5 l 0 17" stroke="#EF4444" strokeWidth="2.4" />
        <path d="M 6.5 -5 l 1 16" stroke="#EF4444" strokeWidth="2.4" />
      </g>
    ),
  },
  walk: {
    color: "green",
    // orange backpack — contrasts the green mascot
    prop: (
      <g>
        <path d="M -10 -4 q 0 -9 10 -9 q 10 0 10 9 v 16 q 0 2 -2 2 h -16 q -2 0 -2 -2 z" fill="#F97316" stroke={O} strokeWidth="2.4" strokeLinejoin="round" />
        <path d="M -10 -2 q 10 -5 20 0" fill="none" stroke={O} strokeWidth="2.2" />
        <rect x="-6" y="3" width="12" height="9" rx="2" fill="#FDBA74" stroke={O} strokeWidth="2" />
        <path d="M 0 4 v 7" stroke={O} strokeWidth="1.8" />
        <path d="M -5 -12 q -3 6 -2 12 M 5 -12 q 3 6 2 12" fill="none" stroke={O} strokeWidth="2" />
      </g>
    ),
  },
  sport: {
    color: "blue",
    // classic football: white ball, black pentagons
    prop: (
      <g>
        <circle cx="0" cy="0" r="12" fill={W} stroke={O} strokeWidth="2.5" />
        {/* centre pentagon */}
        <path d="M 0 -5.5 L 5.2 -1.7 L 3.2 4.4 L -3.2 4.4 L -5.2 -1.7 z" fill={O} />
        {/* edge pentagons (partial) */}
        <path d="M -2.5 -11.5 L 2.5 -11.5 L 4 -8 L 0 -5.5 L -4 -8 z" fill={O} />
        <path d="M 9.5 -7 L 12 -2.5 L 9 1 L 5.2 -1.7 L 7 -6 z" fill={O} />
        <path d="M -9.5 -7 L -7 -6 L -5.2 -1.7 L -9 1 L -12 -2.5 z" fill={O} />
        <path d="M -7 9.5 L -3.2 4.4 L 0 6.5 L 1.5 11 L -4.5 11.5 z" fill={O} />
        <path d="M 7 9.5 L 4.5 11.5 L -0 6.8 L 3.2 4.4 L 6.5 7 z" fill={O} />
      </g>
    ),
  },
  music: {
    color: "red",
    // purple note — contrasts the red mascot
    prop: (
      <g>
        <rect x="2" y="-15" width="3.5" height="20" rx="1.6" fill="#7C3AED" />
        <rect x="11" y="-17" width="3.5" height="20" rx="1.6" fill="#7C3AED" />
        <path d="M 2 -15 l 12 -2 v 5 l -12 2 z" fill="#7C3AED" />
        <ellipse cx="-1.5" cy="6" rx="5.5" ry="4.2" fill="#7C3AED" stroke={O} strokeWidth="1.8" />
        <ellipse cx="7.5" cy="4" rx="5.5" ry="4.2" fill="#7C3AED" stroke={O} strokeWidth="1.8" />
      </g>
    ),
  },
  food: {
    color: "yellow",
    prop: (
      <g>
        <ellipse cx="0" cy="2" rx="13" ry="5" fill={W} stroke={O} strokeWidth="2.2" />
        <ellipse cx="0" cy="0" rx="9" ry="3.5" fill="#FECACA" stroke={O} strokeWidth="1.6" />
        <path d="M -15 -12 v 9 m -3 -9 v 7 q 0 2 3 2 m -3 1 v 14" stroke={O} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <path d="M 16 -12 q 4 5 0 12 v 9" stroke={O} strokeWidth="2.2" fill="none" strokeLinecap="round" />
      </g>
    ),
  },
  drinks: {
    color: "green",
    // amber cocktail — contrasts the green mascot
    prop: (
      <g>
        <path d="M -10 -11 h 20 l -7 11 v 9 h -6 v -9 z" fill="#FBBF24" stroke={O} strokeWidth="2.4" strokeLinejoin="round" />
        <path d="M -8 -11 h 16" stroke={O} strokeWidth="2.4" strokeLinecap="round" />
        <path d="M 0 9 v 5" stroke={O} strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="6" cy="-12" r="2.5" fill="#EF4444" stroke={O} strokeWidth="1.6" />
      </g>
    ),
  },
  beach: {
    color: "blue",
    // yellow sun + navy wave on a blue mascot — clear contrast
    prop: (
      <g>
        <circle cx="0" cy="-5" r="7.5" fill="#FBBF24" stroke={O} strokeWidth="2.4" />
        <g stroke="#F59E0B" strokeWidth="2.2" strokeLinecap="round">
          <path d="M 0 -17 v 3.5 M 0 4 v 3.5 M -12 -5 h 3.5 M 12 -5 h -3.5 M -8.5 -13.5 l 2.5 2.5 M 8.5 -13.5 l -2.5 2.5" />
        </g>
        <path d="M -14 11 q 7 -5 14 0 q 7 5 14 0" fill="none" stroke={O} strokeWidth="2.4" strokeLinecap="round" />
      </g>
    ),
  },
  lecture: {
    color: "blue",
    // white book, red text lines — contrasts the blue mascot
    prop: (
      <g>
        <path d="M 0 -8 q -8 -4 -15 -2 v 16 q 7 -2 15 2 z" fill={W} stroke={O} strokeWidth="2.2" strokeLinejoin="round" />
        <path d="M 0 -8 q 8 -4 15 -2 v 16 q -7 -2 -15 2 z" fill={W} stroke={O} strokeWidth="2.2" strokeLinejoin="round" />
        <path d="M 0 -8 v 16" stroke={O} strokeWidth="2.2" />
        <path d="M -11 -4 h 7 M -11 1 h 7 M 4 -4 h 7 M 4 1 h 7" stroke="#EF4444" strokeWidth="1.6" strokeLinecap="round" />
      </g>
    ),
  },
  party: {
    color: "purple",
    // red hat — contrasts the purple mascot
    prop: (
      <g>
        <path d="M 0 -15 L 8 7 L -8 7 z" fill="#EF4444" stroke={O} strokeWidth="2.4" strokeLinejoin="round" />
        <path d="M -8 7 q 4 -3 8 0 q 4 3 8 0" stroke="#FBBF24" strokeWidth="2.2" fill="none" />
        <circle cx="0" cy="-15" r="2.6" fill="#FBBF24" stroke={O} strokeWidth="1.6" />
        <circle cx="-13" cy="-2" r="2" fill="#FBBF24" />
        <circle cx="13" cy="0" r="2" fill="#2563EB" />
        <circle cx="11" cy="-11" r="2" fill="#059669" />
      </g>
    ),
  },
  art: {
    color: "purple",
    prop: (
      <g>
        <path d="M -12 1 a 12 10 0 1 1 17 9 q -4 1 -4 -2.5 q 0 -3.5 -4.5 -3.5 q -8.5 0 -8.5 -3 z" fill={W} stroke={O} strokeWidth="2.4" />
        <circle cx="-7" cy="-2" r="2.2" fill="#EF4444" />
        <circle cx="-1" cy="-5" r="2.2" fill="#FBBF24" />
        <circle cx="5" cy="-3" r="2.2" fill="#2563EB" />
        <circle cx="-3" cy="4" r="2.2" fill="#059669" />
      </g>
    ),
  },
  nature: {
    color: "blue",
    // green tree on a blue mascot — clear contrast
    prop: (
      <g>
        <circle cx="0" cy="-6" r="10" fill="#34D399" stroke={O} strokeWidth="2.4" />
        <circle cx="-5" cy="-9" r="5" fill="#059669" />
        <circle cx="4" cy="-4" r="4.5" fill="#059669" />
        <rect x="-2.5" y="3" width="5" height="11" rx="2" fill="#92400E" stroke={O} strokeWidth="1.6" />
      </g>
    ),
  },
  // No prop — custom / "Other" formats just show the trio of mascots.
  other: {
    color: "purple",
  },
};

// Only the predefined formats get a themed mascot. Custom free-text formats
// (the "Other → type your own" path) always get the gold-star "other" — it's
// predictable and avoids surprising guesses (e.g. "yoga" must not turn into a
// tree). Specific props for popular custom formats can be added by hand later.
function resolveSpec(format: string | null) {
  return (format && SPEC[format]) || SPEC.other!;
}

// Pick two side-mascot colours that differ from the centre one, for variety.
const PALETTE: Color[] = ["yellow", "blue", "purple", "green", "red"];
function sideColors(center: Color): [Color, Color] {
  const rest = PALETTE.filter((c) => c !== center);
  return [rest[0]!, rest[1]!];
}

/** One mascot (head + dome body) placed/scaled via transform. */
function Body({ c, tx, ty, s }: { c: Color; tx: number; ty: number; s: number }) {
  return (
    <g transform={`translate(${tx} ${ty}) scale(${s})`}>
      <path d="M -30 52 a 30 36 0 0 1 60 0 z" fill={O} transform="translate(0 -6)" />
      <circle cx="0" cy="-14" r="23" fill={O} />
      <path d="M -26 48 a 26 32 0 0 1 52 0 z" fill={FILL[c]} transform="translate(0 -4)" />
      <circle cx="0" cy="-14" r="19" fill={FILL[c]} />
    </g>
  );
}

export function FormatMascot({ format, size = 44 }: { format: string | null; size?: number }) {
  const { color, prop } = resolveSpec(format);
  const [left, right] = sideColors(color);

  return (
    <svg
      viewBox="-50 -50 100 100"
      width={size}
      height={size}
      role="img"
      aria-hidden="true"
      className="select-none"
    >
      <circle cx="0" cy="0" r="50" fill={SOFT[color]} />
      {/* Two side mascots peek out behind the centre one — reads as a group,
          not a single avatar. Drawn first so the centre sits on top. */}
      <Body c={left} tx={-26} ty={14} s={0.62} />
      <Body c={right} tx={26} ty={14} s={0.62} />
      {/* Centre mascot — slightly smaller than before to make room. */}
      <Body c={color} tx={0} ty={4} s={0.82} />
      {/* Prop tucked against the centre mascot's lower-right (predefined formats only). */}
      {prop && <g transform="translate(14 17) scale(0.82)">{prop}</g>}
    </svg>
  );
}
